/**
 * Template Manager Utility
 * 
 * Centralized system for fetching and rendering templates from Firestore.
 * Supports email templates, script variables, and configuration settings.
 */

import { db } from './firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { collectionPath } from './firestoreCollections';

// In-memory cache for templates
const templateCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch a single template by ID
 * @param {string} templateId - Template ID
 * @param {boolean} useCache - Whether to use cached version (default: true)
 * @returns {Promise<Object|null>} Template object or null if not found
 */
export async function getTemplate(templateId, useCache = true) {
  // Check cache first
  if (useCache) {
    const cached = templateCache.get(templateId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
  }
  
  try {
    const docRef = doc(db, collectionPath('templates'), templateId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.warn(`Template not found: ${templateId}`);
      return null;
    }
    
    const template = { id: docSnap.id, ...docSnap.data() };
    
    // Cache the result
    templateCache.set(templateId, {
      data: template,
      timestamp: Date.now()
    });
    
    return template;
  } catch (error) {
    console.error(`Error fetching template ${templateId}:`, error);
    return null;
  }
}

/**
 * Fetch all templates of a specific category
 * @param {string} category - 'email', 'script_variable', 'config'
 * @param {boolean} activeOnly - Only fetch active templates (default: true)
 * @returns {Promise<Array>} Array of template objects
 */
export async function getTemplatesByCategory(category, activeOnly = true) {
  try {
    let q = query(collection(db, collectionPath('systemSettings')), where('category', '==', category));
    
    if (activeOnly) {
      q = query(q, where('isActive', '==', true));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error fetching templates by category ${category}:`, error);
    return [];
  }
}

/**
 * Fetch all templates
 * @param {boolean} activeOnly - Only fetch active templates (default: false)
 * @returns {Promise<Array>} Array of template objects
 */
export async function getAllTemplates(activeOnly = false) {
  try {
    let q = collection(db, collectionPath('templates'));
    
    if (activeOnly) {
      q = query(q, where('isActive', '==', true));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching all templates:', error);
    return [];
  }
}

/**
 * Get a script variable value by template ID
 * @param {string} variableId - Variable template ID (e.g., 'LOOKBACK_DAYS')
 * @param {any} defaultValue - Default value if variable not found
 * @returns {Promise<any>} Variable value
 */
export async function getScriptVariable(variableId, defaultValue = null) {
  const template = await getTemplate(variableId);
  
  if (!template || template.category !== 'script_variable') {
    return defaultValue;
  }
  
  // Return the appropriate value based on type
  if (template.type === 'json' && template.jsonValue) {
    return template.jsonValue;
  }
  
  return template.value ?? defaultValue;
}

/**
 * Render an email template with variable substitution
 * @param {string} templateId - Email template ID
 * @param {Object} variables - Object with variable values
 * @returns {Promise<Object|null>} Rendered email with subject, html, text
 */
export async function renderEmailTemplate(templateId, variables = {}) {
  const template = await getTemplate(templateId);
  
  if (!template || template.category !== 'email') {
    console.error(`Email template not found: ${templateId}`);
    return null;
  }
  
  // Merge with built-in variables
  const allVariables = {
    ...getBuiltInVariables(),
    ...variables
  };
  
  // Render subject
  const subject = replaceVariables(template.subject || '', allVariables);
  
  // Render HTML content
  const htmlContent = replaceVariables(template.htmlContent || '', allVariables);
  
  // Render text content
  const textContent = replaceVariables(template.textContent || '', allVariables);
  
  return {
    subject,
    htmlContent,
    textContent,
    fromName: template.fromName,
    fromEmail: template.fromEmail,
    replyTo: template.replyTo
  };
}

/**
 * Replace variables in a string with {{variableName}} syntax
 * @param {string} content - Content with variable placeholders
 * @param {Object} variables - Object with variable values
 * @returns {string} Content with variables replaced
 */
function replaceVariables(content, variables) {
  let result = content;
  
  // Replace all {{variableName}} occurrences
  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    const value = variables[key];
    
    // Handle different value types
    if (value === null || value === undefined) {
      result = result.replace(regex, '');
    } else if (typeof value === 'object') {
      result = result.replace(regex, JSON.stringify(value));
    } else {
      result = result.replace(regex, String(value));
    }
  });
  
  return result;
}

/**
 * Get built-in template variables
 * @returns {Object} Built-in variables
 */
function getBuiltInVariables() {
  const now = new Date();
  
  return {
    currentDate: now.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    currentTime: now.toLocaleTimeString('en-US'),
    currentYear: now.getFullYear(),
    appName: 'Apparel CRM',
    customerName: 'Sofibella'
  };
}

/**
 * Clear the template cache
 */
export function clearTemplateCache() {
  templateCache.clear();
}

/**
 * Clear a specific template from cache
 * @param {string} templateId - Template ID to clear
 */
export function clearTemplateCacheById(templateId) {
  templateCache.delete(templateId);
}

/**
 * Validate template variables
 * @param {Object} template - Template object
 * @param {Object} providedVariables - Variables provided for rendering
 * @returns {Object} { valid: boolean, missingVars: Array<string> }
 */
export function validateTemplateVariables(template, providedVariables) {
  if (!template.variables || !Array.isArray(template.variables)) {
    return { valid: true, missingVars: [] };
  }
  
  const requiredVars = template.variables.filter(v => !v.startsWith('?')); // ? prefix = optional
  const providedKeys = Object.keys(providedVariables);
  const builtInKeys = Object.keys(getBuiltInVariables());
  const allAvailable = [...providedKeys, ...builtInKeys];
  
  const missingVars = requiredVars.filter(v => !allAvailable.includes(v));
  
  return {
    valid: missingVars.length === 0,
    missingVars
  };
}

/**
 * Extract variables from template content
 * @param {string} content - Template content
 * @returns {Array<string>} Array of variable names found
 */
export function extractVariables(content) {
  if (!content) return [];
  
  const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const matches = content.matchAll(regex);
  const variables = new Set();
  
  for (const match of matches) {
    variables.add(match[1]);
  }
  
  return Array.from(variables);
}

/**
 * Preview template with sample data
 * @param {Object} template - Template object
 * @param {Object} sampleData - Sample variable values
 * @returns {string} Rendered preview
 */
export function previewTemplate(template, sampleData = {}) {
  const defaultSample = {
    status: 'Success',
    orderCount: 42,
    syncTime: 'Wednesday, November 26, 2025 at 2:30 PM',
    duration: '37 seconds',
    customerName: 'John Smith',
    customerName: 'Acme Golf Club',
    ...sampleData
  };
  
  const allVariables = {
    ...getBuiltInVariables(),
    ...defaultSample
  };
  
  if (template.category === 'email') {
    return {
      subject: replaceVariables(template.subject || '', allVariables),
      htmlContent: replaceVariables(template.htmlContent || '', allVariables),
      textContent: replaceVariables(template.textContent || '', allVariables)
    };
  }
  
  return replaceVariables(String(template.value || ''), allVariables);
}

